import type { ConnectError } from '@connectrpc/connect'
import { createConnectQueryKey, useMutation } from '@connectrpc/connect-query'
import { PlanSubmissionStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import {
  getProcurementPlanDetail,
  submitProcurementPlan,
} from '@ebalance/proto/bffuser/v1/procurement_plan_service-ProcurementPlanService_connectquery'
import type { SubmitProcurementPlanResponse } from '@ebalance/proto/bffuser/v1/procurement_plan_service_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { toaster as rawToaster } from '@enechain/ecloud-designsystem'

import { isNetworkError } from '~/libs/isNetworkError'
import {
  resolveSubmitServerError,
  shouldRefetchDetailOnSubmitError,
} from '~/pages/ProcurementPlanDetail/models/serverError'
import { routePaths } from '~/routes/routePaths'

// ecloud toaster の型エイリアス（型解決の問題を回避。GenerationSalesPlanDetail の
// useSubmitGenerationSalesPlan.ts と同パターン）
type AppToaster = {
  create: (options: {
    type?: 'success' | 'error' | 'info' | 'loading' | 'warning'
    title?: string
    description?: string
  }) => string
}
const toaster = rawToaster as unknown as AppToaster

/**
 * 需要調達計画 提出 mutation hook (AC-02)。
 *
 * `ProcurementPlanService.SubmitProcurementPlan` を呼ぶ (= backend
 * EnqueuePlanSubmission への thin passthrough。enqueue 成功で SUBMITTING を
 * 返す非同期提出)。
 *
 * INCONSISTENT な計画を再提出すると backend は新世代の plan を mint し、
 * response の `planId` が元の `planId` と異なる値で返る（元の INCONSISTENT
 * 行は凍結されたまま、新版が SUBMITTING で新規作成される）。この場合、
 * 元の `planId` の detail を再取得し続けても新版の状態は反映されない
 * （旧行は INCONSISTENT のまま）ため、新 `planId` の詳細画面へ navigate
 * する。in-place 経路（`response.planId === planId`）では従来通り当該 plan
 * の detail query を invalidate するのみ。
 *
 * 実際の提出完了 (SUBMITTED / REJECTED / SUBMIT_FAILED) は SendWorker が
 * 非同期に確定するため、以降の遷移はユーザーの再取得・再訪問で反映される。
 */
export function useSubmitProcurementPlan(planId: string): {
  submit: () => void
  isSubmitting: boolean
} {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { mutate, isPending } = useMutation(submitProcurementPlan, {
    onSuccess: async (res: SubmitProcurementPlanResponse): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getProcurementPlanDetail,
          cardinality: 'finite',
        }),
      })
      if (res.planId !== '' && res.planId !== planId) {
        // INCONSISTENT 再提出で新世代が mint された。 新 planId の詳細画面へ
        // 遷移し、 凍結された旧 INCONSISTENT 行の再取得で止まらないようにする。
        void navigate(
          routePaths.planProcurementDetail.path.replace(':id', res.planId),
        )
      }
      toaster.create({
        type: 'success',
        title:
          res.status === PlanSubmissionStatus.APPROVAL_PENDING
            ? t('page:procurementPlanDetail.submit.approvalPendingTitle')
            : t('page:procurementPlanDetail.submit.successTitle'),
      })
    },
    onError: (err: ConnectError): void => {
      if (shouldRefetchDetailOnSubmitError(err)) {
        // AC-PLC-055 / 056 / 085: 画面を開いた後に条件が成立した race。詳細を再取得して
        // 誘導メッセージや押せない理由を出し、提出ボタンを消すか非活性にする。
        void queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getProcurementPlanDetail,
            cardinality: 'finite',
          }),
        })
      }
      toaster.create({
        type: 'error',
        title: t('page:procurementPlanDetail.submit.errorTitle'),
        description:
          resolveSubmitServerError(err, t) ??
          (isNetworkError(err)
            ? t('page:procurementPlanDetail.submit.networkErrorDescription')
            : err.message),
      })
    },
  })

  const submit = (): void => {
    mutate({ planId })
  }

  return { submit, isSubmitting: isPending }
}
